import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowLeft, MagnifyingGlass, X } from '@phosphor-icons/react';
import API from '../lib/api';
import { optimizeImg } from '../lib/image';
import { catalogCache } from '../lib/catalogCache';

const Categories = () => {
  const [cats, setCats] = useState(null); // null = loading
  const [products, setProducts] = useState(() => catalogCache.get() || []);
  const [q, setQ] = useState('');

  useEffect(() => {
    API.get('/categories').then(r => setCats(r.data)).catch(() => setCats([]));
    API.get('/catalog', { params: { summary: 1 } }).then(r => { setProducts(r.data); catalogCache.set(r.data); }).catch(() => {});
  }, []);

  const term = q.trim().toLowerCase();
  const matchedCats = useMemo(
    () => !term ? (cats || []) : (cats || []).filter(c =>
      (c.name || '').toLowerCase().includes(term) ||
      (c.description || '').toLowerCase().includes(term)
    ),
    [cats, term]
  );
  const matchedProducts = useMemo(
    () => !term ? [] : products.filter(p =>
      (p.name || '').toLowerCase().includes(term) ||
      (p.description || '').toLowerCase().includes(term)
    ),
    [products, term]
  );

  return (
    <div className="p-6">
      <Link to="/" className="text-[10px] tracking-[0.24em] uppercase font-bold inline-flex items-center gap-1" data-testid="back-home"><ArrowLeft size={12} /> Back</Link>
      <div className="mt-3 reg-mark">
        <div className="text-[10px] tracking-[0.32em] uppercase font-bold" style={{ color: 'var(--pk-primary)' }}>— Browse categories</div>
        <h1 className="font-display text-[30px] mt-2 leading-tight">What do you need printed?</h1>
      </div>

      <div className="mt-5 relative">
        <MagnifyingGlass size={16} weight="bold" className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-500 pointer-events-none z-10" />
        <input
          value={q}
          onChange={e => setQ(e.target.value)}
          placeholder="Search categories or products…"
          className="pk-input !pl-11 !pr-10"
          data-testid="categories-search"
        />
        {q && (
          <button
            onClick={() => setQ('')}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-1"
            aria-label="Clear search"
            data-testid="categories-search-clear"
          >
            <X size={14} weight="bold" />
          </button>
        )}
      </div>

      {term && (
        <div className="mt-3 text-[10px] tracking-[0.22em] uppercase text-neutral-500">
          {matchedCats.length + matchedProducts.length} result{matchedCats.length + matchedProducts.length === 1 ? '' : 's'} for "{q}"
        </div>
      )}

      {/* Product matches — only when searching */}
      {term && matchedProducts.length > 0 && (
        <div className="mt-4">
          <div className="text-[10px] tracking-[0.24em] uppercase font-bold" style={{ color: 'var(--pk-primary)' }}>— Products</div>
          <div className="mt-2 grid grid-cols-2 gap-3">
            {matchedProducts.slice(0, 8).map(p => (
              <Link
                key={p.product_id}
                to={`/shop/${p.product_id}`}
                className="pk-border bg-white brutal-shadow-sm"
                data-testid={`search-product-${p.product_id}`}
              >
                <div className="aspect-square overflow-hidden bg-neutral-100">
                  {p.image && (
                    <img
                      src={optimizeImg(p.image, 400)}
                      alt={p.name}
                      width="400"
                      height="400"
                      loading="lazy"
                      decoding="async"
                      className="w-full h-full object-cover"
                    />
                  )}
                </div>
                <div className="p-3 border-t-2 border-black">
                  <div className="text-[9px] tracking-[0.22em] uppercase text-neutral-500">{(p.pricing_mode || '').replace('_', ' ')}</div>
                  <div className="font-bold text-[13px] mt-1 leading-tight">{p.name}</div>
                  <div className="mt-1 flex items-center justify-between">
                    <div className="font-mono text-[12px]">From <b>₹{p.base_price}</b></div>
                    <ArrowRight size={14} weight="bold" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Category list */}
      <div className="mt-5">
        {term && matchedCats.length > 0 && (
          <div className="text-[10px] tracking-[0.24em] uppercase font-bold mb-2" style={{ color: 'var(--pk-primary)' }}>— Categories</div>
        )}
        <div className="space-y-4">
          {cats === null && Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="pk-card brutal-shadow-sm overflow-hidden animate-pulse" data-testid={`category-skeleton-${i}`}>
              <div className="aspect-[16/9] bg-neutral-200"></div>
              <div className="p-4 border-t-2 border-black space-y-2">
                <div className="h-2 bg-neutral-200 w-20"></div>
                <div className="h-4 bg-neutral-200 w-32"></div>
                <div className="h-3 bg-neutral-200 w-48"></div>
              </div>
            </div>
          ))}
          {matchedCats.map((c, i) => (
            <Link key={c.category_id} to={`/shop?category=${c.category_id}`} className="pk-card brutal-shadow-sm overflow-hidden block" data-testid={`category-${c.slug}`}>
              <div className="aspect-[16/9] bg-neutral-100 overflow-hidden">
                {c.image && (
                  <img
                    src={optimizeImg(c.image, 640)}
                    alt={c.name}
                    width="640"
                    height="360"
                    loading={i < 2 ? 'eager' : 'lazy'}
                    fetchpriority={i < 1 ? 'high' : 'auto'}
                    decoding="async"
                    className="w-full h-full object-cover"
                  />
                )}
              </div>
              <div className="p-4 border-t-2 border-black flex items-center justify-between">
                <div>
                  <div className="text-[9px] tracking-[0.24em] uppercase text-neutral-500">Category {String(i+1).padStart(2,'0')}</div>
                  <div className="font-display text-[20px] mt-1 leading-none">{c.name}</div>
                  <div className="text-[12px] text-neutral-600 mt-1">{c.description}</div>
                </div>
                <ArrowRight size={20} weight="bold" />
              </div>
            </Link>
          ))}
        </div>

        {term && matchedCats.length === 0 && matchedProducts.length === 0 && (
          <div className="text-center py-10 text-[13px] text-neutral-500" data-testid="categories-search-empty">
            No matches for "{q}". Try another keyword.
          </div>
        )}
      </div>
    </div>
  );
};

export default Categories;
